<template>
  <div>
    <div class="text-center">
      <div>
        <v-img
          src="../assets/email_inbox.png"
          class="my-3 mt-8"
          height="200"
          alt="email inbox"
        ></v-img>
      </div>
      <h1 class="text-grey-darken-2 mt-10">Verify your email address</h1>
      <p class="text-grey-darken-2 text-subtitle">
        Please check your inbox, we have sent a verification email to
        {{ email }}
      </p>

      <v-container fluid>
        <v-row align="center" justify="center" no-gutters>
          <v-col cols="12" sm="6" md="5" xl="3">
            <v-alert
              v-if="rateLimited"
              type="warning"
              variant="tonal"
              density="compact"
              class="text-left"
            >
              Too many checks from this device. Please wait a few minutes, then
              use the button below once you've clicked the verification link.
            </v-alert>
            <v-alert
              v-else-if="pollingGaveUp"
              type="info"
              variant="tonal"
              density="compact"
              class="text-left"
            >
              Already clicked the link in your email? Tap below to continue.
            </v-alert>
          </v-col>
        </v-row>
        <v-row align="center" justify="center" no-gutters>
          <v-col cols="12" sm="6" md="4" xl="2">
            <v-btn
              v-if="rateLimited || pollingGaveUp"
              size="small"
              class="mb-4"
              color="primary"
              variant="flat"
              block
              :loading="manualCheckLoading"
              @click="manualCheck"
            >
              I've verified, continue
            </v-btn>
            <v-btn
              size="small"
              class="mb-16"
              variant="tonal"
              block
              @click="resendVerificationEmail"
              :disabled="resendSecondsLeft > 0"
            >
              Resend verification email<template v-if="resendSecondsLeft > 0">
                in {{ resendSecondsLeft }}s</template
              >
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import { auth, sendEmailVerification } from "../auth";
import { capture } from "../analytics";

// backoff instead of a fixed interval, and far fewer attempts than before:
// polling every 30s for up to 30 attempts (~17 minutes of continuous Firebase
// auth calls) was tripping Firebase's per-device "unusual activity" block
// (auth/too-many-requests), which then left the user on a screen that never
// explained what happened
const POLL_BASE_MS = 15000;
const POLL_MAX_MS = 120000;
const POLL_BACKOFF_FACTOR = 1.8;
const MAX_POLL_ATTEMPTS = 6;
// Firebase refuses another verification email for about a minute after the
// last one (the API sends the first at signup): in production, 1 of 66 resends
// made in the first minute on this screen went through
const RESEND_COOLDOWN_MS = 60000;

export default {
  data() {
    return {
      email: "loading...",
      activeInterval: undefined,
      resendSecondsLeft: 0,
      pollAttempts: 0,
      isMounted: false,
      resendCountdown: undefined,
      rateLimited: false,
      pollingGaveUp: false,
      manualCheckLoading: false,
      visibilityListener: undefined,
    };
  },
  computed: {
    ...mapGetters(["getUserEmail"]),
  },
  beforeUnmount() {
    this.isMounted = false;
    this.stopPolling();
    this.stopResendCountdown();
    if (this.visibilityListener) {
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.visibilityListener = undefined;
    }
  },
  mounted() {
    this.email = this.getUserEmail;
    this.isMounted = true;

    this.$store.commit("setIsLoading", false);
    capture("verify_email_viewed");

    this.scheduleNextPoll();

    // the API sent the first email at signup, so a reload or a return days
    // later waits only what is left of that minute. Right after signup the
    // user has no metadata yet, which means the email has just gone out
    const signedUpAt = Date.parse(
      this.$store.state.user?.metadata?.creationTime,
    );
    this.lockResend(Number.isNaN(signedUpAt) ? Date.now() : signedUpAt);
  },
  methods: {
    lockResend(lastSentAt = Date.now()) {
      this.stopResendCountdown();
      const availableAt = Math.min(lastSentAt, Date.now()) + RESEND_COOLDOWN_MS;
      // from the deadline, not by counting ticks: a background tab runs its
      // timers late
      const tick = () => {
        this.resendSecondsLeft = Math.max(
          0,
          Math.ceil((availableAt - Date.now()) / 1000),
        );
        if (this.resendSecondsLeft === 0) this.stopResendCountdown();
      };
      tick();
      if (this.resendSecondsLeft > 0) {
        this.resendCountdown = setInterval(tick, 1000);
      }
    },
    stopResendCountdown() {
      if (this.resendCountdown) {
        clearInterval(this.resendCountdown);
        this.resendCountdown = undefined;
      }
    },
    stopPolling() {
      if (this.activeInterval) {
        clearTimeout(this.activeInterval);
        this.activeInterval = undefined;
      }
    },
    scheduleNextPoll() {
      if (this.pollAttempts >= MAX_POLL_ATTEMPTS) {
        this.pollingGaveUp = true;
        capture("verify_email_blocked", {
          reason: "polling_gave_up",
          source: "auto_poll",
          poll_attempts: this.pollAttempts,
        });
        return;
      }
      const delay = Math.min(
        POLL_BASE_MS * Math.pow(POLL_BACKOFF_FACTOR, this.pollAttempts),
        POLL_MAX_MS,
      );
      const jitter = Math.random() * 5000;
      this.activeInterval = setTimeout(() => {
        if (document.hidden) {
          // don't spend a poll (and a Firebase call) on a backgrounded tab;
          // resume as soon as it's visible again instead
          this.waitForVisibleThenPoll();
          return;
        }
        this.pollUserVerification();
      }, delay + jitter);
    },
    waitForVisibleThenPoll() {
      if (this.visibilityListener) return;
      this.visibilityListener = () => {
        if (document.hidden) return;
        document.removeEventListener(
          "visibilitychange",
          this.visibilityListener,
        );
        this.visibilityListener = undefined;
        if (this.isMounted) {
          this.pollUserVerification();
        }
      };
      document.addEventListener("visibilitychange", this.visibilityListener);
    },
    async checkVerifiedAndRedirect() {
      const currentUser = auth().currentUser;
      if (!currentUser) {
        return false;
      }
      await currentUser.reload();
      if (!this.isMounted) return false;
      const userAfter = auth().currentUser;
      if (userAfter && userAfter.emailVerified === true) {
        this.stopPolling();
        this.$store.commit("setUser", userAfter);
        this.$router.push("/").catch((err) => {
          if (err && err.name !== "NavigationDuplicated") {
            console.warn(err);
          }
        });
        return true;
      }
      return false;
    },
    async pollUserVerification() {
      this.pollAttempts++;
      try {
        const verified = await this.checkVerifiedAndRedirect();
        if (verified || !this.isMounted) return;
      } catch (err) {
        if (!this.isMounted) return;
        console.error("email verification poll failed", err);
        if (err && err.code === "auth/too-many-requests") {
          // Firebase is actively blocking this device: retrying on a timer
          // will just repeat the same error, so stop and let the user act
          // manually instead
          this.rateLimited = true;
          capture("verify_email_blocked", {
            reason: "rate_limited",
            source: "auto_poll",
            poll_attempts: this.pollAttempts,
          });
          this.stopPolling();
          return;
        }
      }
      this.scheduleNextPoll();
    },
    async manualCheck() {
      this.manualCheckLoading = true;
      try {
        const verified = await this.checkVerifiedAndRedirect();
        if (verified) {
          this.rateLimited = false;
          this.pollingGaveUp = false;
        }
      } catch (err) {
        console.error("manual verification check failed", err);
        if (err && err.code === "auth/too-many-requests") {
          this.rateLimited = true;
          capture("verify_email_blocked", {
            reason: "rate_limited",
            source: "manual_check",
            poll_attempts: this.pollAttempts,
          });
        } else {
          this.$store.commit("showSnackbarMessage", {
            content:
              "Couldn't check verification status, please try again in a moment.",
            color: "error",
          });
        }
      } finally {
        this.manualCheckLoading = false;
      }
    },
    async resendVerificationEmail() {
      this.lockResend();

      try {
        await sendEmailVerification();
        this.$store.commit("showSnackbarMessage", {
          content: "Verification email sent",
          color: "success",
        });
        capture("verify_email_resend_clicked", { succeeded: true });
      } catch (err) {
        console.error("failed to send verification email", err);
        const tooSoon = err && err.code === "auth/too-many-requests";
        const content = tooSoon
          ? "Please wait a minute before sending another email."
          : "Failed to send verification email, please try again.";
        this.$store.commit("showSnackbarMessage", { content, color: "error" });
        capture("verify_email_resend_clicked", {
          succeeded: false,
          code: err && err.code,
        });
        if (!tooSoon) {
          this.stopResendCountdown();
          this.resendSecondsLeft = 0;
        }
        return;
      }

      if (!this.isMounted) return;
      this.stopPolling();
      this.pollAttempts = 0;
      this.rateLimited = false;
      this.pollingGaveUp = false;
      this.scheduleNextPoll();
    },
  },
};
</script>
